<template>
  <div class="session-timeout-duration-field">
    <BittsInput
      v-model="amount"
      :name="`${name}-amount`"
      :form-label="{ title: label, helpText }"
      :placeholder="defaultPlaceholder"
      :status="hasError ? 'danger' : 'default'"
      :danger-text="errorMessage"
      :disabled="isDisabled"
      :data-testid="`${name}-amount`"
      inputmode="numeric"
      class="session-timeout-duration-field__amount"
    />
    <BittsSelect
      v-model="unitSecs"
      :options="UNIT_OPTIONS"
      :form-label="{ title: 'Unit' }"
      :aria-label="`Unit for ${label}`"
      :disabled="isDisabled"
      :data-testid="`${name}-unit`"
      class="session-timeout-duration-field__unit"
    />
  </div>
</template>

<script setup lang="ts">
import { BittsInput, BittsSelect } from '@crossbeam/bitts';

import { computed, ref, watch } from 'vue';

import {
  SECONDS_PER_DAY,
  SECONDS_PER_HOUR,
  SECONDS_PER_MINUTE,
  formatDuration,
  largestExactUnit,
} from '@/utils/sessionDuration';

const props = defineProps<{
  label: string;
  helpText: string;
  name: string;
  defaultSecs: number;
  errorMessage: string;
  isDisabled: boolean;
}>();

const secs = defineModel<number | null>({ required: true });
const hasInvalidAmount = defineModel<boolean>('hasInvalidAmount', {
  required: true,
});

const UNIT_OPTIONS = [
  { label: 'Minutes', value: SECONDS_PER_MINUTE },
  { label: 'Hours', value: SECONDS_PER_HOUR },
  { label: 'Days', value: SECONDS_PER_DAY },
];

const unitSecs = ref(
  secs.value ? largestExactUnit(secs.value) : SECONDS_PER_HOUR,
);
const amount = ref(secs.value ? String(secs.value / unitSecs.value) : '');

const defaultPlaceholder = computed(
  () => `Default (${formatDuration(props.defaultSecs)})`,
);

const hasError = computed(() => !!props.errorMessage);

function isPositiveWholeNumber(text: string) {
  return /^\d+$/.test(text) && Number(text) > 0;
}

watch([amount, unitSecs], () => {
  const trimmed = amount.value.trim();
  const isInvalid = !!trimmed && !isPositiveWholeNumber(trimmed);
  hasInvalidAmount.value = isInvalid;
  secs.value = trimmed && !isInvalid ? Number(trimmed) * unitSecs.value : null;
});
</script>

<style scoped lang="pcss">
@reference "../css/theme.css";
.session-timeout-duration-field {
  @apply flex items-start gap-8 max-w-[420px];
}
.session-timeout-duration-field__amount {
  @apply flex-1;
}
.session-timeout-duration-field__unit {
  @apply w-[120px] shrink-0;
}
</style>
